import { useEffect, useRef } from 'react'
import type { TouchEvent as ReactTouchEvent } from 'react'
import DetailsPanel from './DetailsPanel'
import { useIsMobile } from '../lib/useMobile'
import { lockBodyScroll, unlockBodyScroll } from '../lib/bodyScroll'

type DetailsProps = {
  open: boolean
  onClose: () => void
  id: string | null
  wiki?: string | null
}

export default function Details({ open, onClose, id, wiki }: DetailsProps) {
  const isMobile = useIsMobile()
  const panelRef = useRef<HTMLElement | null>(null)
  const startXRef = useRef(0)
  const deltaXRef = useRef(0)

  useEffect(() => {
    if (!isMobile) return
    if (open) {
      lockBodyScroll()
      return () => unlockBodyScroll()
    }
    unlockBodyScroll()
    return undefined
  }, [isMobile, open])

  useEffect(() => () => unlockBodyScroll(), [])

  const handleTouchStart = (event: ReactTouchEvent<HTMLDivElement>) => {
    if (!isMobile || !open) return
    startXRef.current = event.touches[0]?.clientX ?? 0
    deltaXRef.current = 0
    if (panelRef.current) {
      panelRef.current.style.transition = 'none'
    }
  }

  const handleTouchMove = (event: ReactTouchEvent<HTMLDivElement>) => {
    if (!isMobile || !open) return
    const currentX = event.touches[0]?.clientX ?? 0
    const delta = currentX - startXRef.current
    deltaXRef.current = delta
    if (delta > 0 && panelRef.current) {
      event.preventDefault()
      const clamped = Math.min(Math.max(delta, 0), 260)
      panelRef.current.style.transform = `translateX(${clamped}px)`
    }
  }

  const handleTouchEnd = () => {
    if (!isMobile || !open) return
    const panel = panelRef.current
    if (panel) {
      panel.style.transition = ''
      panel.style.transform = ''
    }
    if (deltaXRef.current > 80) {
      onClose()
    }
    deltaXRef.current = 0
  }

  const panelClasses = [
    'fixed md:static top-14 bottom-0 right-0 md:inset-auto z-40 md:z-auto',
    'flex',
    'w-[88vw] max-w-[460px] md:w-[360px]',
    'bg-black/60 md:bg-transparent',
    'backdrop-blur md:backdrop-blur-0',
    'border-l border-white/10 md:border-none',
    'pt-[calc(env(safe-area-inset-top)+16px)]',
    'pb-[calc(env(safe-area-inset-bottom)+16px)]',
    'px-4 md:px-0',
    'transition-transform duration-300 ease-out md:transition-none',
    open ? 'pointer-events-auto' : 'pointer-events-none',
    open ? 'translate-x-0' : 'translate-x-[95vw]',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <>
      {isMobile && open && (
        <button
          type="button"
          onClick={onClose}
          className="fixed inset-0 z-30 bg-black/40 md:hidden"
          aria-label="Close details"
        />
      )}
      <section
        ref={panelRef}
        className={panelClasses}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        <div className="flex h-full w-full flex-col overflow-hidden rounded-2xl border border-neutral-800 bg-ink-1/80 backdrop-blur-md shadow-glow md:rounded-none md:border-none md:bg-transparent md:backdrop-blur-0 md:shadow-none">
          <DetailsPanel
            id={id}
            wiki={wiki}
            className="flex h-full flex-col"
            onClose={onClose}
            mobile={isMobile}
          />
        </div>
      </section>
    </>
  )
}
